import * as _ from "lodash";
import { DateTime } from "luxon";
import { Nullable } from "../types/Nullable";
import { TimezoneIANAEnum, timezoneIANAs } from "../types/TimezoneIANAEnum";
import { printDevError } from "./print-dev-error";
import { toValidLuxonDateTimeFromEventDate } from "./dates/event-date-to-luxon-datetime";
import { EventDateResponse } from "modules/api-docs";

export const getLuxonTimeDiffNow = (
  compareDateTime: DateTime,
  currentDateTime: DateTime,
) => {
  return compareDateTime.diff(currentDateTime).valueOf();
};

const dateToMidnightDate = (date: Date | string) => {
  const midnightDate = new Date(date);
  midnightDate.setHours(0, 0, 0, 0);
  return midnightDate;
};

export const currentDate = () => dateToMidnightDate(new Date());

export const currentDay = () => currentDate().toISOString();

export const getTimeFromLuxonDateTime = (date: DateTime) => {
  return date?.toJSDate().getTime();
};

export const getLuxonDateTimeFromISO = (dateString: string) => {
  return DateTime.fromISO(dateString);
};

export const isValidDateString = (dateString: string) => {
  const dateTime: DateTime = DateTime.fromISO(dateString);
  return dateTime.isValid;
};

export const getDateStringInFormat = (
  dateString: string,
  oldDateFormat: string,
  newDateFormat: string,
): string => {
  const dt = DateTime.fromFormat(dateString, oldDateFormat);
  return dt.toFormat(newDateFormat);
};

export function getRangeOfYears(range: number) {
  let currentYear = DateTime.now().year;
  let minYear = currentYear - range;
  let maxYear = currentYear + range;

  return _.range(minYear, maxYear);
}

export function isCurrentDateAfterStartDate(openTime: string | undefined) {
  return DateTime.now() >= DateTime.fromISO(openTime ?? "");
}

export function isCurrentDateOnOrAfterStartDateTime(openTime: DateTime) {
  return DateTime.now() >= openTime;
}

export type TimezoneInfo = {
  timezoneIANA: Nullable<TimezoneIANAEnum>;
  formattedTimezone: string;
};
export const MISSING_TIMEZONE: TimezoneInfo = {
  timezoneIANA: null,
  formattedTimezone: "Missing timezone",
};
const computeMissingTimezoneReturn: (message?: string) => TimezoneInfo = (
  message,
) =>
  message
    ? {
        timezoneIANA: null,
        formattedTimezone: message ?? "Missing timezone",
      }
    : MISSING_TIMEZONE;

/**
 *
 * @param dt an IANA-zoned `DateTime`
 * @param opts
 * @returns the IANA timezone (`null` if not IANA-zoned)
 */
export function extractTimezoneFromDateTime(
  dt: DateTime,
  opts?: {
    format?: Intl.DateTimeFormatOptions["timeZoneName"];
    missingTimezoneMessage?: string;
  },
): TimezoneInfo {
  if (dt?.zone?.type !== "iana") {
    printDevError(`You must use an IANA zone: ${dt}`);
    return computeMissingTimezoneReturn(opts?.missingTimezoneMessage);
  }

  const timezonePart = dt
    .toLocaleParts({ timeZoneName: opts?.format ?? "shortGeneric" })
    .filter((part) => part.type === "timeZoneName")?.[0]?.value;

  // Shouldn't happen ever
  if (!timezonePart) {
    printDevError(`The timezone locale part is missing: ${dt.zone}`);
    return computeMissingTimezoneReturn(opts?.missingTimezoneMessage);
  }

  // Shouldn't happen since we use IANAs between FE and BE
  if (!timezoneIANAs.includes(dt.zoneName as TimezoneIANAEnum)) {
    printDevError(`The timezone is not in our list IANAs: ${dt.zoneName}`);
    return computeMissingTimezoneReturn(opts?.missingTimezoneMessage);
  }

  return {
    timezoneIANA: dt.zoneName as TimezoneIANAEnum,
    formattedTimezone: timezonePart,
  };
}

export const createScheduledSlotDateTimeStr = (
  startDateTime: EventDateResponse,
  endDateTime: EventDateResponse,
) => {
  return (
    createScheduledSlotDateStr(startDateTime) +
    ", " +
    createScheduledSlotTimeStr(startDateTime, endDateTime)
  );
};

const createScheduledSlotDateStr = (startDateTime: EventDateResponse) => {
  const startLuxonDt: DateTime | undefined = toValidLuxonDateTimeFromEventDate(
    startDateTime?.date ?? "",
    startDateTime?.timezone ?? "",
  ).extract();

  const dateStr: string = "" + startLuxonDt?.toFormat("LLL dd, yyyy");

  return dateStr;
};

const createScheduledSlotTimeStr = (
  startDateTime: EventDateResponse,
  endDateTime: EventDateResponse,
) => {
  const startLuxonDt: DateTime | undefined = toValidLuxonDateTimeFromEventDate(
    startDateTime?.date ?? "",
    startDateTime?.timezone ?? "",
  ).extract();

  const endLuxonDt: DateTime | undefined = toValidLuxonDateTimeFromEventDate(
    endDateTime?.date ?? "",
    endDateTime?.timezone ?? "",
  ).extract();

  const timeStrWithTz: string =
    "" +
    startLuxonDt?.toFormat("h:mm a") +
    " - " +
    endLuxonDt?.toFormat("h:mm a") +
    " " +
    startLuxonDt?.toFormat("ZZZZ");

  return timeStrWithTz;
};
